/**
 * Coach Weekly Digest — pure builder.
 *
 * Produces a single WeeklyDigest object from:
 *   - The trader's trade history (for performance grounding)
 *   - The current Coach Profile (current edges/leaks)
 *   - The profile snapshot history (for week-over-week movement)
 *   - getTradeNetPnL callback for commission-aware netPnl
 *
 * Different from the dashboard's "since last snapshot" diff:
 *   - Dashboard compares CURRENT vs IMMEDIATELY-PRIOR snapshot (could be hours ago)
 *   - Digest compares CURRENT vs the snapshot closest to 7 days ago (a real week)
 *   - Dashboard surfaces signal status; digest surfaces NARRATIVE — "this week's
 *     story" — anchored in performance numbers from the trades themselves
 *
 * Pure: takes inputs in, returns a digest object out. No I/O.
 *
 * Returns null if there's not enough data for ANY meaningful section
 * (no trades AND no profile). Otherwise returns a digest with a
 * `sections` flag indicating what's present so the renderer can
 * gracefully omit empty parts.
 */

import { signatureOf } from '../profile/buildProfile.js';
import { getCurrentTradingDay, addDaysToDateStr } from '../../timeService.js';

const DAY_MS = 24 * 60 * 60 * 1000;
export const WEEK_MS = 7 * DAY_MS;
// Digest window length in trading days. Stays at 7 — it's "weekly digest"
// after all — but pulled to a constant so a future "rolling 14-day" mode
// can swap it without touching window math everywhere.
export const DIGEST_WINDOW_DAYS = 7;

/**
 * Find the snapshot closest to (now - 7 days) for week-over-week comparison.
 * History is newest-first per storeProfile's invariant. We want the most
 * recent snapshot that is OLDER than the cutoff — that's our "a week ago"
 * baseline.
 *
 * If no snapshot is old enough, fall back to the OLDEST available snapshot
 * (degraded mode — the digest still works, but the comparison window is
 * shorter than a week).
 *
 * Returns null when history is empty.
 */
export const findWeekAgoSnapshot = (history, anchorMs = Date.now()) => {
  if (!Array.isArray(history) || history.length === 0) return null;
  const cutoff = anchorMs - WEEK_MS;
  for (const snap of history) {
    const t = Date.parse(snap?.generatedAt || '');
    if (Number.isFinite(t) && t <= cutoff) return snap;
  }
  // No snapshot old enough — return the OLDEST so we have SOME baseline.
  return history[history.length - 1];
};

/**
 * Filter trades to a date window. trade.date is the project-wide
 * `YYYY-MM-DD` trading-day key (per CLAUDE.md), so string-compare against
 * the window bounds — never `new Date(trade.date)`.
 */
const tradesInWindow = (trades, startDateStr, endDateStr) => {
  if (!Array.isArray(trades)) return [];
  return trades.filter(t =>
    t?.date && t.date >= startDateStr && t.date <= endDateStr
  );
};

/**
 * Convert a Date (or epoch ms) into a YYYY-MM-DD string in the host's
 * local time. Sufficient for windowing — this isn't the trade-day boundary
 * machinery, just a date label for the comparison range.
 */
const toDateStr = (input) => {
  const d = input instanceof Date ? input : new Date(input);
  if (!Number.isFinite(d.getTime())) return null;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const isWin = (netPnl) => Number.isFinite(netPnl) && netPnl > 0;

/**
 * Compute performance stats from a list of trades. Uses the supplied
 * getTradeNetPnL callback so commission-aware netPnl is consistent with
 * the rest of the app (Coach scopes already enrich on the same callback).
 *
 * Returns null when no trades fall in the window.
 */
const computePerformance = (trades, getTradeNetPnL, getAccountById) => {
  if (!Array.isArray(trades) || trades.length === 0) return null;
  const enriched = trades.map(t => {
    const acc = getAccountById ? getAccountById(t.accountId) : null;
    const r = getTradeNetPnL ? getTradeNetPnL(t, acc) : { netPnl: parseFloat(t.pnl) || 0 };
    return { ...t, netPnl: Number.isFinite(r?.netPnl) ? r.netPnl : 0 };
  });

  let netPnl = 0;
  let wins = 0;
  let losses = 0;
  let biggestWin = null;
  let biggestLoss = null;
  let bestWinNet = -Infinity;
  let worstLossNet = Infinity;
  const rs = [];
  // Setup attribution
  const setupAgg = new Map(); // setupName → { count, netPnl, wins }
  for (const t of enriched) {
    netPnl += t.netPnl;
    if (isWin(t.netPnl)) {
      wins++;
      if (t.netPnl > bestWinNet) { bestWinNet = t.netPnl; biggestWin = t; }
    } else {
      losses++;
      if (t.netPnl < worstLossNet) { worstLossNet = t.netPnl; biggestLoss = t; }
    }
    const r = parseFloat(t.rMultiple);
    if (Number.isFinite(r)) rs.push(r);
    if (t.setup) {
      if (!setupAgg.has(t.setup)) setupAgg.set(t.setup, { name: t.setup, count: 0, netPnl: 0, wins: 0 });
      const a = setupAgg.get(t.setup);
      a.count++;
      a.netPnl += t.netPnl;
      if (isWin(t.netPnl)) a.wins++;
    }
  }

  const setups = [...setupAgg.values()];
  setups.sort((a, b) => b.netPnl - a.netPnl);
  const bestSetup = setups[0] || null;
  const worstSetup = setups.length > 0 ? setups[setups.length - 1] : null;

  return {
    tradeCount: enriched.length,
    netPnl,
    wins,
    losses,
    winRate: enriched.length > 0 ? wins / enriched.length : 0,
    avgR: rs.length > 0 ? rs.reduce((s, r) => s + r, 0) / rs.length : null,
    biggestWin: biggestWin ? { tradeId: biggestWin.id, date: biggestWin.date, netPnl: biggestWin.netPnl, setup: biggestWin.setup } : null,
    biggestLoss: biggestLoss ? { tradeId: biggestLoss.id, date: biggestLoss.date, netPnl: biggestLoss.netPnl, setup: biggestLoss.setup } : null,
    bestSetup,
    worstSetup: worstSetup && worstSetup !== bestSetup ? worstSetup : null,
  };
};

/**
 * Compare current profile against the week-ago snapshot to produce
 * movement buckets. Mirrors diffProfile's classification logic but
 * returns thinner objects (no priorEntry refs) suitable for digest
 * rendering.
 */
const computeProfileMovement = (currentProfile, weekAgoSnap) => {
  const movement = {
    newEdges: [], persistedEdges: [], fadedEdges: [],
    newLeaks: [], persistedLeaks: [], resolvedLeaks: [],
    regressed: [], ascended: [],
    hasComparable: false,
  };
  if (!currentProfile) return movement;

  const curEdges = new Map();
  const curLeaks = new Map();
  for (const e of (currentProfile.edges || [])) curEdges.set(e.signature, e);
  for (const l of (currentProfile.leaks || [])) curLeaks.set(l.signature, l);

  if (!weekAgoSnap) {
    // Without a comparable snapshot, EVERYTHING in current is treated as
    // "this week's edges/leaks" — no week-over-week deltas computed.
    movement.persistedEdges = [...curEdges.values()];
    movement.persistedLeaks = [...curLeaks.values()];
    return movement;
  }

  movement.hasComparable = true;
  const priorEdges = new Set(weekAgoSnap.edgeSignatures || []);
  const priorLeaks = new Set(weekAgoSnap.leakSignatures || []);

  // Walk current edges
  for (const [sig, e] of curEdges) {
    if (priorEdges.has(sig)) movement.persistedEdges.push(e);
    else if (priorLeaks.has(sig)) movement.ascended.push(e);   // leak → edge
    else movement.newEdges.push(e);
  }
  // Walk current leaks
  for (const [sig, l] of curLeaks) {
    if (priorLeaks.has(sig)) movement.persistedLeaks.push(l);
    else if (priorEdges.has(sig)) movement.regressed.push(l);  // edge → leak
    else movement.newLeaks.push(l);
  }
  // Faded: in priorEdges but not in current edges OR leaks (pure absence)
  for (const sig of priorEdges) {
    if (!curEdges.has(sig) && !curLeaks.has(sig)) {
      movement.fadedEdges.push({ signature: sig });
    }
  }
  // Resolved: in priorLeaks but not in current leaks OR edges
  for (const sig of priorLeaks) {
    if (!curLeaks.has(sig) && !curEdges.has(sig)) {
      movement.resolvedLeaks.push({ signature: sig });
    }
  }
  return movement;
};

/**
 * Pick the 1-3 most actionable items for next week. Prioritizes
 * regressions + persistent leaks (things bleeding repeatedly), then new
 * leaks (fresh issues to nip early). Each item carries the source
 * observation so the renderer can drill in.
 */
const computeRecommendations = (movement) => {
  const recs = [];
  // Regressions are loudest — an edge that flipped to a leak this week
  for (const r of movement.regressed.slice(0, 1)) {
    if (r.action) recs.push({ title: r.title, action: r.action, source: r, kind: 'regression' });
  }
  // Persistent leaks with high occurrence — chronic issues
  const persistentByOcc = [...movement.persistedLeaks].sort((a, b) => (b.occurrences || 1) - (a.occurrences || 1));
  for (const l of persistentByOcc.slice(0, 1)) {
    if (l.action && (l.occurrences || 1) >= 2) {
      recs.push({ title: l.title, action: l.action, source: l, kind: 'persistent' });
    }
  }
  // New leaks — fresh issues
  for (const l of movement.newLeaks.slice(0, 1)) {
    if (l.action) recs.push({ title: l.title, action: l.action, source: l, kind: 'new' });
  }
  // Cap to 3
  return recs.slice(0, 3);
};

/**
 * Build a single-sentence headline summarizing the week.
 * Templated; the LLM narrator gets a richer turn at the top of the modal.
 */
const computeHeadline = (performance, movement) => {
  const fmtMoney = (n) => `${n >= 0 ? '+' : '−'}$${Math.abs(Math.round(n)).toLocaleString()}`;
  const fmtPct = (n) => `${Math.round(n * 100)}%`;

  if (performance && performance.tradeCount > 0) {
    const profitable = performance.netPnl >= 0;
    const wrPart = `${fmtPct(performance.winRate)} WR across ${performance.tradeCount} trade${performance.tradeCount === 1 ? '' : 's'}`;
    const movePart = movement.regressed.length > 0
      ? `, with ${movement.regressed.length} edge${movement.regressed.length === 1 ? '' : 's'} regressing to leak${movement.regressed.length === 1 ? '' : 's'}`
      : movement.resolvedLeaks.length > 0
        ? `, with ${movement.resolvedLeaks.length} leak${movement.resolvedLeaks.length === 1 ? '' : 's'} resolved`
        : '';
    return `${fmtMoney(performance.netPnl)} on the week — ${wrPart}${movePart}.`;
  }
  if (movement.hasComparable) {
    const parts = [];
    if (movement.newEdges.length > 0) parts.push(`${movement.newEdges.length} new edge${movement.newEdges.length === 1 ? '' : 's'}`);
    if (movement.newLeaks.length > 0) parts.push(`${movement.newLeaks.length} new leak${movement.newLeaks.length === 1 ? '' : 's'}`);
    if (movement.resolvedLeaks.length > 0) parts.push(`${movement.resolvedLeaks.length} leak${movement.resolvedLeaks.length === 1 ? '' : 's'} resolved`);
    if (parts.length > 0) return `Coach view since last week: ${parts.join(', ')}.`;
  }
  return 'Quiet week — no trades and no major profile changes to report.';
};

/**
 * Public API: build a WeeklyDigest from the current data layer.
 *
 * The digest window is anchored to the current TRADING DAY (ET-anchored
 * via `getCurrentTradingDay()` per CLAUDE.md's storage contract), NOT the
 * host clock's calendar date. Without this, opening the digest at 11:55pm
 * ET would use yesterday's calendar date as the window end and miss trades
 * stamped against today's trading session.
 *
 * Anchor override:
 *   - `anchorDateStr` (preferred, new in Phase 14) — explicit YYYY-MM-DD
 *     trading-day key. Tests pass this for determinism.
 *   - `anchor` (legacy) — Date or epoch ms. Kept for back-compat with
 *     callers that haven't migrated. Converted to a YYYY-MM-DD via host
 *     local time, which is fine because the conversion happens once at
 *     the boundary and downstream window math stays in trading-day-key
 *     space.
 *
 * @param {Object} args
 * @param {Array}  args.trades                  All closed trades available
 * @param {Object} args.profile                 Current CoachProfile
 * @param {Array}  args.profileHistory          History snapshots (newest-first compact form)
 * @param {Function} args.getTradeNetPnL
 * @param {Function} [args.getAccountById]
 * @param {string} [args.anchorDateStr]         Explicit YYYY-MM-DD trading-day anchor (preferred)
 * @param {Date|number} [args.anchor]           Legacy Date/epoch fallback for back-compat
 * @returns {WeeklyDigest|null}
 */
export const buildWeeklyDigest = ({
  trades = [],
  profile = null,
  profileHistory = [],
  getTradeNetPnL,
  getAccountById,
  anchorDateStr,
  anchor,
} = {}) => {
  // Resolve the anchor trading day. Prefer the explicit string; fall back
  // to deriving from the legacy `anchor` Date; finally default to today's
  // trading day per ET-boundary semantics.
  let endDateStr;
  if (anchorDateStr && /^\d{4}-\d{2}-\d{2}$/.test(anchorDateStr)) {
    endDateStr = anchorDateStr;
  } else if (anchor != null) {
    const d = new Date(anchor);
    if (!Number.isFinite(d.getTime())) return null;
    endDateStr = toDateStr(d);
  } else {
    endDateStr = getCurrentTradingDay();
  }
  if (!endDateStr) return null;

  // Window start = end - DIGEST_WINDOW_DAYS via trading-day-key arithmetic.
  // String-only math; no Date construction, no host-local drift.
  const startDateStr = addDaysToDateStr(endDateStr, -DIGEST_WINDOW_DAYS);

  // Anchor ms used by the snapshot picker (history snapshots store ISO
  // timestamps so we still need a real ms value). Derive from the trading-
  // day end at noon ET as a representative midpoint. We don't use this for
  // trade windowing — that stays in YYYY-MM-DD string space.
  const [endY, endM, endD] = endDateStr.split('-').map(n => parseInt(n, 10));
  const anchorMs = Number.isFinite(endY)
    ? new Date(Date.UTC(endY, endM - 1, endD, 16, 0, 0)).getTime()  // ~noon ET ≈ 16:00 UTC
    : Date.now();

  const weekTrades = tradesInWindow(trades, startDateStr, endDateStr);
  const performance = computePerformance(weekTrades, getTradeNetPnL, getAccountById);
  const weekAgoSnap = findWeekAgoSnapshot(profileHistory, anchorMs);
  const movement = computeProfileMovement(profile, weekAgoSnap);
  const headline = computeHeadline(performance, movement);
  const recommendations = computeRecommendations(movement);

  // If there's literally nothing to show, return null so the caller can
  // render an empty-state message instead of a hollow digest.
  const hasAny =
    !!performance ||
    movement.persistedEdges.length > 0 ||
    movement.persistedLeaks.length > 0 ||
    movement.newEdges.length > 0 ||
    movement.newLeaks.length > 0 ||
    movement.resolvedLeaks.length > 0;
  if (!hasAny) return null;

  // Window ISO bounds derived from the trading-day-key bounds. windowStart
  // = start-day at 18:00 ET (futures session open) ≈ 22:00 UTC; windowEnd
  // = end-day at 17:00 ET (futures session close) ≈ 21:00 UTC. Rough but
  // good enough for the renderer's "May 8 → May 15" display label —
  // exact ET anchoring isn't critical for the human-readable date range.
  const isoForTradingDay = (dateStr, hourUtc) => {
    const [y, m, d] = (dateStr || '').split('-').map(n => parseInt(n, 10));
    if (!Number.isFinite(y)) return null;
    return new Date(Date.UTC(y, m - 1, d, hourUtc, 0, 0)).toISOString();
  };

  return {
    generatedAt: new Date().toISOString(),
    windowStart: isoForTradingDay(startDateStr, 22),  // session open ≈ 18:00 ET
    windowEnd:   isoForTradingDay(endDateStr, 21),    // session close ≈ 17:00 ET
    windowStartDate: startDateStr,
    windowEndDate: endDateStr,
    performance,
    movement,
    headline,
    recommendations,
    hasComparableSnapshot: movement.hasComparable,
    weekAgoSnapshotAt: weekAgoSnap?.generatedAt || null,
    sections: {
      hasPerformance: !!performance,
      hasMovement: movement.hasComparable,
      hasRecommendations: recommendations.length > 0,
    },
  };
};

// Exports for tests
export {
  tradesInWindow,
  toDateStr,
  computePerformance,
  computeProfileMovement,
  computeRecommendations,
  computeHeadline,
};
